import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { BookOpen, Plus, ScaleIcon as Scale } from "lucide-react";
import { formatMoney, formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { DatePicker } from "@/components/ui/calendar";

const TYPE_BADGE_CLASS = {
  asset: "bg-[var(--info-bg)] text-[var(--info)] border-0 text-xs",
  expense: "bg-[var(--warning-bg)] text-[var(--warning)] border-0 text-xs",
  liability: "bg-[var(--error-bg)] text-[var(--error)] border-0 text-xs",
  income: "bg-[var(--success-bg)] text-[var(--success)] border-0 text-xs",
  equity: "bg-[var(--accent-bg)] text-[var(--accent)] border-0 text-xs",
};
const TYPE_COLORS = {
  asset: "info",
  expense: "warning",
  liability: "error",
  income: "success",
  equity: "accent",
};
const TYPE_LABEL_KEYS = {
  asset: "asset",
  expense: "expense",
  liability: "liability",
  income: "income",
  equity: "equity",
};

export default function GeneralLedger() {
  const { t } = useI18n();
  return (
    <div
      className="min-w-0 max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8"
      data-testid="gl-page"
    >
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
          {t("finance.generalLedger")}
        </h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t("finance.chartAccounts")}, {t("finance.journal").toLowerCase()},{" "}
          {t("finance.trialBalance").toLowerCase()}
        </p>
      </div>
      <Tabs defaultValue="trial">
        <TabsList className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="trial" data-testid="tab-trial">
            <Scale className="h-3.5 w-3.5 mr-1.5" /> {t("finance.trialBalance")}
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="tab-journal">
            <BookOpen className="h-3.5 w-3.5 mr-1.5" /> {t("finance.journal")}
          </TabsTrigger>
          <TabsTrigger value="accounts" data-testid="tab-accounts">
            {t("finance.chartAccounts")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="trial">
          <TrialTab />
        </TabsContent>
        <TabsContent value="journal">
          <JournalTab />
        </TabsContent>
        <TabsContent value="accounts">
          <AccountsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function TrialTab() {
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ["trial-balance"],
    queryFn: async () => unwrap(await api.get("/gl/trial-balance")),
  });
  const accts = data?.accounts || [];
  return (
    <div className="mt-6">
      <Card className="min-w-0 border-[var(--subtle)]">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("finance.trialBalance")}
          </CardTitle>
          <Badge
            className={
              data?.balanced
                ? "bg-[var(--success-bg)] text-[var(--success)] border-0"
                : "bg-[var(--error-bg)] text-[var(--error)] border-0"
            }
          >
            {data?.balanced
              ? `✓ ${t("finance.balanced")}`
              : `✗ ${t("finance.notBalanced")}`}
          </Badge>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">{t("finance.code")}</th>
                <th className="text-left px-3 py-3">{t("finance.account")}</th>
                <th className="text-left px-3 py-3">{t("finance.type")}</th>
                <th className="text-right px-3 py-3">{t("finance.debit")}</th>
                <th className="text-right px-3 py-3">{t("finance.credit")}</th>
              </tr>
            </thead>
            <tbody>
              {accts.map((a) => {
                const bal = a.balance_paise || 0;
                const dr = a.type === "asset" || a.type === "expense" ? bal : 0;
                const cr = a.type === "asset" || a.type === "expense" ? 0 : bal;
                return (
                  <tr key={a.id} data-testid={`acct-row-${a.code}`}>
                    <td className="px-3 py-3 font-mono-num text-xs">
                      {a.code}
                    </td>
                    <td className="px-3 py-3 font-medium">{a.name}</td>
                    <td className="px-3 py-3">
                      <Badge
                        className={
                          TYPE_BADGE_CLASS[a.type] ||
                          "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0 text-xs"
                        }
                      >
                        {t(`finance.${TYPE_LABEL_KEYS[a.type] || a.type}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-num">
                      {dr > 0 ? formatMoney(dr) : "—"}
                    </td>
                    <td className="px-3 py-3 text-right font-mono-num">
                      {cr > 0 ? formatMoney(cr) : "—"}
                    </td>
                  </tr>
                );
              })}
              <tr className="font-bold border-t-2 border-[var(--text-primary)]">
                <td colSpan={3} className="px-3 py-3 text-right">
                  {t("finance.total")}
                </td>
                <td className="px-3 py-3 text-right font-mono-num">
                  {formatMoney(data?.total_debit_paise || 0)}
                </td>
                <td className="px-3 py-3 text-right font-mono-num">
                  {formatMoney(data?.total_credit_paise || 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function JournalTab() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["journal-entries"],
    queryFn: async () => unwrap(await api.get("/gl/journal-entries")),
  });
  const { data: accts } = useQuery({
    queryKey: ["gl-accounts"],
    queryFn: async () => unwrap(await api.get("/gl/accounts")),
  });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    narration: "",
    reference: "",
    lines: [
      { account_code: "", debit_paise: 0, credit_paise: 0, memo: "" },
      { account_code: "", debit_paise: 0, credit_paise: 0, memo: "" },
    ],
  });
  const totalD = form.lines.reduce(
    (s, l) => s + (Number(l.debit_paise) || 0),
    0,
  );
  const totalC = form.lines.reduce(
    (s, l) => s + (Number(l.credit_paise) || 0),
    0,
  );
  const balanced = totalD === totalC && totalD > 0;
  const post = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.post("/gl/journal-entries", {
          ...form,
          lines: form.lines.map((l) => ({
            account_code: l.account_code,
            debit_paise: Number(l.debit_paise) || 0,
            credit_paise: Number(l.credit_paise) || 0,
            memo: l.memo,
          })),
        }),
      ),
    onSuccess: () => {
      toast.success(t("finance.journalPosted"));
      setShow(false);
      setForm({
        ...form,
        narration: "",
        lines: [
          { account_code: "", debit_paise: 0, credit_paise: 0, memo: "" },
          { account_code: "", debit_paise: 0, credit_paise: 0, memo: "" },
        ],
      });
      qc.invalidateQueries({ queryKey: ["journal-entries"] });
      qc.invalidateQueries({ queryKey: ["trial-balance"] });
      qc.invalidateQueries({ queryKey: ["gl-accounts"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const updLine = (i, key, val) => {
    const ln = [...form.lines];
    ln[i] = { ...ln[i], [key]: val };
    setForm({ ...form, lines: ln });
  };
  const addLine = () =>
    setForm({
      ...form,
      lines: [
        ...form.lines,
        { account_code: "", debit_paise: 0, credit_paise: 0, memo: "" },
      ],
    });
  return (
    <div className="mt-6 space-y-4">
      <Card className="border-[var(--subtle)]">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-sm font-display">
            {t("finance.journal")} {t("finance.lines").toLowerCase()}
          </CardTitle>
          <Dialog open={show} onOpenChange={setShow}>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="new-journal-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> {t("finance.postEntry")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{t("finance.newJournalEntry")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <DatePicker
                    value={form.date}
                    onChange={(date) => setForm({ ...form, date })}
                    testId="je-date"
                  />
                  <Input
                    placeholder={`${t("finance.reference")} (${t("finance.optional")})`}
                    value={form.reference}
                    onChange={(e) =>
                      setForm({ ...form, reference: e.target.value })
                    }
                    data-testid="je-ref"
                  />
                </div>
                <Input
                  placeholder={t("finance.narration")}
                  value={form.narration}
                  onChange={(e) =>
                    setForm({ ...form, narration: e.target.value })
                  }
                  data-testid="je-narration"
                />
                <div className="space-y-2">
                  {form.lines.map((l, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 items-center"
                    >
                      <div className="col-span-5">
                        <Select
                          value={l.account_code}
                          onValueChange={(v) => updLine(i, "account_code", v)}
                        >
                          <SelectTrigger data-testid={`je-line-${i}-account`}>
                            <SelectValue placeholder={t("finance.account")} />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {(accts || []).map((a) => (
                              <SelectItem key={a.code} value={a.code}>
                                {a.code} · {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input
                        className="col-span-3"
                        type="number"
                        placeholder={t("finance.debitPaise")}
                        value={l.debit_paise || ""}
                        onChange={(e) =>
                          updLine(i, "debit_paise", e.target.value)
                        }
                        data-testid={`je-line-${i}-debit`}
                      />
                      <Input
                        className="col-span-3"
                        type="number"
                        placeholder={t("finance.creditPaise")}
                        value={l.credit_paise || ""}
                        onChange={(e) =>
                          updLine(i, "credit_paise", e.target.value)
                        }
                        data-testid={`je-line-${i}-credit`}
                      />
                    </div>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addLine}
                    data-testid="je-add-line"
                  >
                    + {t("finance.addLine")}
                  </Button>
                </div>
                <div
                  className={`text-sm font-mono-num ${balanced ? "text-[var(--success)]" : "text-[var(--error)]"}`}
                >
                  {t("finance.totalDebit")}: {formatMoney(totalD)} ·{" "}
                  {t("finance.totalCredit")}: {formatMoney(totalC)} ·{" "}
                  {balanced
                    ? `✓ ${t("finance.balanced")}`
                    : t("finance.notBalancedShort")}
                </div>
              </div>
              <DialogFooter>
                <Button
                  onClick={() => post.mutate()}
                  disabled={!balanced || !form.narration || post.isPending}
                  data-testid="je-save"
                >
                  {t("finance.postEntry")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">{t("finance.date")}</th>
                <th className="text-left px-3 py-3">
                  {t("finance.narration")}
                </th>
                <th className="text-left px-3 py-3">
                  {t("finance.reference")}
                </th>
                <th className="text-right px-3 py-3">{t("finance.amount")}</th>
                <th className="text-left px-3 py-3">{t("finance.lines")}</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((j) => (
                <tr key={j.id} data-testid={`je-${j.id}`}>
                  <td className="px-3 py-3 font-mono-num text-xs">
                    {formatDate(j.date)}
                  </td>
                  <td className="px-3 py-3 font-medium">{j.narration}</td>
                  <td className="px-3 py-3 text-[var(--text-muted)] font-mono-num text-xs">
                    {j.reference || "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num">
                    {formatMoney(j.total_paise)}
                  </td>
                  <td className="px-3 py-3 text-xs text-[var(--text-secondary)]">
                    {(j.lines || [])
                      .map(
                        (l) =>
                          `${l.account_code}(${l.debit_paise > 0 ? t("finance.debitShort") : t("finance.creditShort")} ${formatMoney(l.debit_paise || l.credit_paise)})`,
                      )
                      .join(" · ")}
                  </td>
                </tr>
              ))}
              {(data || []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-8 text-center text-[var(--text-muted)]"
                  >
                    {t("finance.noJournalEntries")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function AccountsTab() {
  const { t } = useI18n();
  const { data } = useQuery({
    queryKey: ["gl-accounts"],
    queryFn: async () => unwrap(await api.get("/gl/accounts")),
  });
  return (
    <div className="mt-6">
      <Card className="border-[var(--subtle)]">
        <CardHeader>
          <CardTitle className="text-sm font-display">
            {t("finance.accountCount", { count: (data || []).length })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-3">{t("finance.code")}</th>
                <th className="text-left px-3 py-3">{t("finance.name")}</th>
                <th className="text-left px-3 py-3">{t("finance.type")}</th>
                <th className="text-right px-3 py-3">{t("finance.balance")}</th>
              </tr>
            </thead>
            <tbody>
              {(data || []).map((a) => (
                <tr key={a.id} data-testid={`acct-${a.code}`}>
                  <td className="px-3 py-3 font-mono-num">{a.code}</td>
                  <td className="px-3 py-3 font-medium">{a.name}</td>
                  <td className="px-3 py-3">
                    <Badge
                      className={
                        TYPE_BADGE_CLASS[a.type] ||
                        "bg-[var(--subtle-bg)] text-[var(--text-muted)] border-0 text-xs"
                      }
                    >
                      {t(`finance.${TYPE_LABEL_KEYS[a.type] || a.type}`)}
                    </Badge>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num">
                    {formatMoney(a.balance_paise || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
